import { useEffect, useState } from 'react';
import { getAccountLocale } from '@/utils/locale/getAccountLocale';
import { ICountryData } from '@/types';
import { shallow } from 'zustand/shallow';
import { updateLocale } from '@/utils/locale/updateLocale';
import { useRouter } from '../useRouter/useRouter';
import { useAuthStore } from '@/store/authStore/authStore';
import { usePhrasesStore } from '@/store/phrasesStore';
import { useModalStore } from '@/store/modalStore';
import { useCurrentPath } from '../useCurrentPath/useCurrentPath';
import { useLocale } from '../useLocale/useLocale';

export const useSwitchLocale = (
    preferredLanguageId: number | null | undefined,
    countryCode: string | undefined,
    countries: ICountryData[],
    isDisabled?: boolean,
) => {
    const isLoggedIn = useAuthStore((s) => s.loggedIn);
    const router = useRouter();
    const currentPath = useCurrentPath();
    const i18nLocale = useLocale();
    const { setLocale } = usePhrasesStore((s) => ({
        setLocale: s.setLocale,
    }));

    useEffect(() => {
        if (!countryCode || isDisabled) return;

        const accountLocale = getAccountLocale(preferredLanguageId, countryCode, countries);
        if (!accountLocale) return;

        void updateLocale({
            locale: accountLocale,
            isLoggedIn,
            router,
            currentPath,
            i18nLocale,
            setLocale,
        });
    }, [
        countryCode,
        countries,
        isLoggedIn,
        router,
        currentPath,
        i18nLocale,
        setLocale,
        isDisabled,
        preferredLanguageId,
    ]);
};

export const useSwitchLocaleConfirm = (
    preferredLanguageId: number | null | undefined,
    countryCode: string | undefined,
    countries: ICountryData[],
) => {
    const { updateModal } = useModalStore(
        (s) => ({
            updateModal: s.updateModal,
        }),
        shallow,
    );
    const { locale: storedLocale } = usePhrasesStore();
    const locale = getAccountLocale(preferredLanguageId, countryCode, countries);
    const [prevLocale, setPrevLocale] = useState<string | undefined>(undefined);

    if (prevLocale !== locale && !storedLocale.hasBeenChecked) {
        setPrevLocale(locale);
        updateModal('localeConfirm', {
            isOpen: Boolean(locale && locale !== storedLocale.locale),
            details: { selectedLocale: locale, isLoggedIn: false },
        });
    }

    return locale;
};
